import { useState, useEffect, useCallback } from 'react';
import { rentals } from '../services/api';

export const useAllRentals = (refreshTrigger) => {
  const [allRentals, setAllRentals] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [lastFetchTime, setLastFetchTime] = useState(null);

  // Fetch all rentals function
  const fetchAllRentals = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      // Fetch all rentals without pagination by setting a high limit
      const params = {
        limit: 10000, // High limit to get all rentals
        page: 1
      };
      
      const response = await rentals.getAll(params);
      
      if (response.data && response.data.rentals) {
        // New pagination response format
        setAllRentals(response.data.rentals);
      } else if (Array.isArray(response.data)) {
        // Fallback for old response format
        setAllRentals(response.data);
      } else {
        throw new Error('Invalid response format');
      }
      
      setLastFetchTime(new Date().toISOString());
    } catch (err) {
      console.error('Error fetching all rentals:', err);
      setError(err.message || 'Failed to fetch rentals');
      setAllRentals([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Fetch rentals for a specific date range (for calendar views)
  const fetchRentalsForDateRange = useCallback(async (startDate, endDate) => {
    setIsLoading(true);
    setError(null);
    
    try {
      const params = {
        limit: 10000, // High limit to get all rentals in range
        page: 1,
        startDate: startDate ? startDate.toISOString() : '',
        endDate: endDate ? endDate.toISOString() : '',
        dateFilter: 'custom'
      };
      
      // Remove empty parameters
      Object.keys(params).forEach(key => {
        if (params[key] === '' || params[key] === undefined || params[key] === null) {
          delete params[key];
        }
      });
      
      const response = await rentals.getAll(params);
      
      if (response.data && response.data.rentals) {
        setAllRentals(response.data.rentals);
      } else if (Array.isArray(response.data)) {
        setAllRentals(response.data);
      } else {
        throw new Error('Invalid response format');
      }
      
      setLastFetchTime(new Date().toISOString());
    } catch (err) {
      console.error('Error fetching rentals for date range:', err);
      setError(err.message || 'Failed to fetch rentals');
      setAllRentals([]);
    } finally {
      setIsLoading(false);
    }
  }, []);
  // Auto-fetch on mount
  useEffect(() => {
    fetchAllRentals();
  }, [fetchAllRentals]);

  // Refresh when external trigger changes
  useEffect(() => {
    if (refreshTrigger) {
      fetchAllRentals();
    }
  }, [refreshTrigger, fetchAllRentals]);

  return {
    allRentals,
    isLoading,
    error,
    lastFetchTime,
    refetch: fetchAllRentals,
    fetchRentalsForDateRange
  };
};
